import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';

const C = {
  bg: '#050510', surface: '#0d0d1f', border: 'rgba(255,255,255,0.06)',
  primary: '#7c6ff7', accent: '#00d4ff', green: '#00e676', amber: '#ffb800', danger: '#ff4466',
  text: '#f0f0ff', text2: '#8888aa', text3: '#444466',
};

interface PublicPipeline {
  title?: string;
  idea?: { aiOutput?: Record<string, unknown>; rawIdea?: string };
  realityCheck?: { aiOutput?: Record<string, unknown> };
  positioning?: { aiOutput?: Record<string, unknown> };
  status?: string;
}

export default function PublicResultsPage() {
  const { id } = useParams<{ id: string }>();
  const [pipeline, setPipeline] = useState<PublicPipeline | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/pipeline/${id}/public`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setPipeline(data.data.pipeline);
        else setError('This pipeline is not publicly available.');
      })
      .catch(() => setError('Could not load results.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: `conic-gradient(${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`, animation: 'spin 1.2s linear infinite', position: 'relative' }}>
          <div style={{ width: 34, height: 34, background: C.bg, borderRadius: '50%', position: 'absolute', top: 7, left: 7 }} />
        </div>
      </div>
    );
  }

  if (error || !pipeline) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.text2, padding: 24 }}>
        <LogoLockup size="lg" />
        <p style={{ marginTop: 24, fontSize: 16 }}>{error || 'Results not found.'}</p>
        <a href="/" style={{ color: C.primary, marginTop: 16, fontSize: 14 }}>← Back to StartupScore</a>
      </div>
    );
  }

  const name = pipeline.title || (pipeline.idea?.aiOutput?.ideaSnapshot as string) || 'Startup Idea';
  const validationScore = (pipeline.realityCheck?.aiOutput?.validationScore as number) ?? null;
  const scores = (pipeline.realityCheck?.aiOutput?.scores as Record<string, number>) ?? {};

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '60px 24px', color: C.text }}>
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <LogoLockup size="md" />
        <div style={{ marginTop: 32, marginBottom: 16, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.primary }}>
          Validation Results
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, marginBottom: 16 }}>{name}</h1>
        {validationScore !== null && (
          <div style={{ background: `${C.green}18`, border: `1px solid ${C.green}40`, borderRadius: 12, padding: '8px 20px', marginBottom: 24, display: 'inline-block' }}>
            <span style={{ color: C.green, fontSize: 16, fontWeight: 700 }}>Score: {validationScore}/100</span>
          </div>
        )}
        {Object.entries(scores).length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(Object.keys(scores).length, 4)}, 1fr)`, gap: 10, marginBottom: 32 }}>
            {Object.entries(scores).map(([key, score]) => (
              <div key={key} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 8px' }}>
                <div style={{ color: score >= 70 ? C.green : score >= 50 ? C.amber : C.danger, fontSize: 24, fontWeight: 800 }}>{score}</div>
                <div style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 24, marginTop: 24 }}>
          <p style={{ color: C.text2, fontSize: 14, marginBottom: 16 }}>
            Want to validate your own startup idea?
          </p>
          <a href="/auth?mode=signup" style={{
            display: 'inline-block', background: C.primary, color: '#fff', borderRadius: 12,
            padding: '12px 28px', fontSize: 15, fontWeight: 700, textDecoration: 'none',
          }}>
            Get Started Free
          </a>
        </div>
      </div>
    </div>
  );
}
